import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileSearch, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  activateOfficialReadinessTrial,
  createReadinessTrial,
  documentReadinessIncident,
  generateReadinessReport,
  getReadinessEligibleAccounts,
  getReadinessIncidents,
  getReadinessReports,
  getReadinessTrials,
} from '../../services/api.admin';
import { getApiErrorMessage } from '../../lib/apiErrors';


const initialDraft = {
  trading_account_id: '',
  entry_owner: 'strategy_automation',
  account_strategy_id: '',
  symbol: 'MNQ',
  strategy_name: '',
  timeframe_minutes: '5',
  quantity: '1',
  calendar_verified_through: '',
  git_commit: '',
  backend_image_digest: '',
  finalization_delay_hours: '2',
  pnl_tolerance: '2',
  protection_max_seconds: '15',
  job_max_age_seconds: '30',
};


const statusClass = (status) => {
  if (status === 'PASS' || status === 'completed') return 'border-emerald-500/30 text-emerald-300';
  if (status === 'FAIL' || status === 'rca_required') return 'border-red-500/30 text-red-300';
  return 'border-amber-500/30 text-amber-300';
};


const ReadinessTrialPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [trials, setTrials] = useState([]);
  const [reports, setReports] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [draft, setDraft] = useState(initialDraft);
  const [rcaDrafts, setRcaDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [accountRes, trialRes, reportRes, incidentRes] = await Promise.all([
        getReadinessEligibleAccounts(),
        getReadinessTrials(),
        getReadinessReports({ limit: 50 }),
        getReadinessIncidents({ status: 'open' }),
      ]);
      setAccounts(accountRes.data?.accounts || []);
      setTrials(trialRes.data?.trials || []);
      setReports(reportRes.data?.reports || []);
      setIncidents(incidentRes.data?.incidents || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not load readiness evidence'));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedAccount = useMemo(
    () => accounts.find((row) => row.id === draft.trading_account_id),
    [accounts, draft.trading_account_id],
  );
  const availableLinks = selectedAccount?.strategy_links || [];
  const unfinishedTrials = useMemo(
    () => trials.filter((row) => ['active', 'rca_required', 'evidence_interrupted'].includes(row.status)),
    [trials],
  );

  const setField = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const selectAccount = (accountId) => {
    const account = accounts.find((row) => row.id === accountId);
    const firstLink = account?.strategy_links?.[0];
    setDraft((current) => ({
      ...current,
      trading_account_id: accountId,
      account_strategy_id: firstLink?.id || '',
      symbol: firstLink?.symbol || current.symbol,
      strategy_name: firstLink?.strategy_name || '',
      timeframe_minutes: String(firstLink?.timeframe_minutes || 5),
      quantity: String(firstLink?.quantity || 1),
    }));
  };

  const selectLink = (linkId) => {
    const link = availableLinks.find((row) => row.id === linkId);
    if (!link) return;
    setDraft((current) => ({
      ...current,
      account_strategy_id: link.id,
      symbol: link.symbol,
      strategy_name: link.strategy_name,
      timeframe_minutes: String(link.timeframe_minutes),
      quantity: String(link.quantity),
    }));
  };

  const handleCreate = async () => {
    setBusy(true);
    try {
      await createReadinessTrial({
        trading_account_id: draft.trading_account_id,
        mode: 'shadow',
        manifest: {
          symbol: draft.symbol,
          strategy_name: draft.entry_owner === 'ai_guided' ? 'AI Research Trader' : draft.strategy_name,
          timeframe_minutes: Number(draft.timeframe_minutes),
          quantity: Number(draft.quantity),
          entry_owner: draft.entry_owner,
          calendar_verified_through: draft.calendar_verified_through,
          git_commit: draft.git_commit,
          backend_image_digest: draft.backend_image_digest,
          finalization_delay_hours: Number(draft.finalization_delay_hours),
          thresholds: {
            pnl_tolerance: Number(draft.pnl_tolerance),
            protection_max_seconds: Number(draft.protection_max_seconds),
            job_max_age_seconds: Number(draft.job_max_age_seconds),
          },
        },
      });
      toast.success('Shadow readiness trial created');
      setDraft(initialDraft);
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not create readiness trial'));
    } finally {
      setBusy(false);
    }
  };

  const generate = async (trialId, phase) => {
    setBusy(true);
    try {
      await generateReadinessReport(trialId, { phase });
      toast.success(`${phase === 'final' ? 'Final' : 'Preliminary'} report generated`);
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not generate report'));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (trialId) => {
    setBusy(true);
    try {
      await activateOfficialReadinessTrial(trialId);
      toast.success('Official ten-session trial started');
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not activate official trial'));
    } finally {
      setBusy(false);
    }
  };

  const documentIncident = async (incidentId) => {
    const values = rcaDrafts[incidentId] || {};
    setBusy(true);
    try {
      await documentReadinessIncident(incidentId, values);
      toast.success('RCA documented; evidence clock explicitly restarted');
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not document RCA'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Practice Readiness Evidence</CardTitle>
              <CardDescription>Broker-authoritative shadow and ten-session trial reports. This panel never changes trading controls.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={busy}>
              <RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {unfinishedTrials.length === 0 ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label className="space-y-1 text-xs text-zinc-400">
                Practice account
                <Select value={draft.trading_account_id} onValueChange={selectAccount}>
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>{accounts.map((row) => <SelectItem key={row.id} value={row.id}>{row.account_name || row.external_account_id}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              <label className="space-y-1 text-xs text-zinc-400">
                Entry owner
                <Select value={draft.entry_owner} onValueChange={(value) => setField('entry_owner', value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="strategy_automation">Strategy automation</SelectItem><SelectItem value="ai_guided">AI-guided trading</SelectItem></SelectContent>
                </Select>
              </label>
              {draft.entry_owner === 'strategy_automation' ? (
                <label className="space-y-1 text-xs text-zinc-400 md:col-span-2">
                  Frozen strategy link
                  <Select value={draft.account_strategy_id} onValueChange={selectLink}>
                    <SelectTrigger><SelectValue placeholder="Select active link" /></SelectTrigger>
                    <SelectContent>{availableLinks.map((row) => <SelectItem key={row.id} value={row.id}>{row.strategy_name} · {row.symbol} · {row.timeframe_minutes}m · qty {row.quantity}</SelectItem>)}</SelectContent>
                  </Select>
                </label>
              ) : null}
              {draft.entry_owner === 'ai_guided' ? <Input value={draft.symbol} onChange={(event) => setField('symbol', event.target.value.toUpperCase())} placeholder="Micro symbol" /> : null}
              <Input type="date" value={draft.calendar_verified_through} onChange={(event) => setField('calendar_verified_through', event.target.value)} aria-label="Calendar verified through" />
              <Input value={draft.git_commit} onChange={(event) => setField('git_commit', event.target.value)} placeholder="Git commit SHA" />
              <Input value={draft.backend_image_digest} onChange={(event) => setField('backend_image_digest', event.target.value)} placeholder="Backend image digest" />
              <Input type="number" min="0" step="0.5" value={draft.finalization_delay_hours} onChange={(event) => setField('finalization_delay_hours', event.target.value)} placeholder="Final delay hours" />
              <Input type="number" min="0" step="0.01" value={draft.pnl_tolerance} onChange={(event) => setField('pnl_tolerance', event.target.value)} placeholder="P&L tolerance" />
              <label className="space-y-1 text-xs text-zinc-400">
                Protection critical max (seconds)
                <Input type="number" min="0" value={draft.protection_max_seconds} onChange={(event) => setField('protection_max_seconds', event.target.value)} />
                <span className="block text-[11px] text-zinc-500">Default 15s. Exceeding this fails the session.</span>
              </label>
              <Input type="number" min="0" value={draft.job_max_age_seconds} onChange={(event) => setField('job_max_age_seconds', event.target.value)} placeholder="Job max age seconds" />
              <div className="md:col-span-2 xl:col-span-4 flex justify-end">
                <Button onClick={handleCreate} disabled={busy || !draft.trading_account_id}>Create Frozen Shadow Trial</Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {unfinishedTrials.map((trial) => (
                <div key={trial.id} className="rounded-md border border-zinc-800 bg-zinc-950/50 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div><div className="font-semibold">{trial.manifest?.account_name || trial.trading_account_id}</div><div className="text-xs text-zinc-500">{trial.manifest?.symbol} · {trial.manifest?.strategy_name} · {trial.manifest?.timeframe_minutes}m</div></div>
                    <Badge variant="outline" className={statusClass(trial.status)}>{trial.mode} · {trial.status}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><div className="text-zinc-500">Clean finals</div><div className="text-lg font-semibold">{trial.clean_session_count}/{trial.target_clean_sessions}</div></div>
                    <div><div className="text-zinc-500">Quantity</div><div className="text-lg font-semibold">{trial.manifest?.quantity}</div></div>
                    <div><div className="text-zinc-500">Owner</div><div className="font-medium">{trial.manifest?.entry_owner}</div></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => generate(trial.id, 'preliminary')} disabled={busy}><FileSearch className="mr-2 h-4 w-4" /> Preliminary</Button>
                    <Button size="sm" variant="outline" onClick={() => generate(trial.id, 'final')} disabled={busy}>Generate Final</Button>
                    {trial.mode === 'shadow' && trial.status === 'active' ? <Button size="sm" onClick={() => activate(trial.id)} disabled={busy}>Start Official Trial</Button> : null}
                  </div>
                  <div className="break-all text-[11px] text-zinc-600">Manifest {trial.manifest_hash}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {incidents.map((incident) => (
        <Card key={incident.id} className="border-red-500/30 bg-red-950/10">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><AlertTriangle className="h-4 w-4 text-red-400" /> {incident.reason_code}</CardTitle><CardDescription>{incident.summary}</CardDescription></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            {['root_cause', 'corrective_action', 'verification_evidence'].map((field) => (
              <textarea key={field} className="min-h-24 rounded-md border border-zinc-700 bg-zinc-950 p-3 text-sm" value={rcaDrafts[incident.id]?.[field] || ''} onChange={(event) => setRcaDrafts((current) => ({ ...current, [incident.id]: { ...(current[incident.id] || {}), [field]: event.target.value } }))} placeholder={field.replaceAll('_', ' ')} />
            ))}
            <div className="md:col-span-3 flex justify-end"><Button variant="destructive" onClick={() => documentIncident(incident.id)} disabled={busy}>Document RCA and Restart Evidence Clock</Button></div>
          </CardContent>
        </Card>
      ))}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader><CardTitle className="text-base">Recent Session Reports</CardTitle><CardDescription>Only final official PASS reports advance the streak.</CardDescription></CardHeader>
        <CardContent>
          {reports.length === 0 ? <div className="text-sm text-zinc-500">No readiness reports yet.</div> : (
            <div className="space-y-2">
              {reports.map((report) => (
                <details key={report.id} className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
                  <summary className="cursor-pointer list-none flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2">{report.status === 'PASS' ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertTriangle className="h-4 w-4 text-amber-400" />}<strong>{report.cme_trade_date}</strong><span className="text-zinc-500">{report.phase} v{report.report_version}</span></span>
                    <Badge variant="outline" className={statusClass(report.status)}>{report.status}</Badge>
                  </summary>
                  <div className="mt-3 grid gap-3 md:grid-cols-3 text-xs">
                    <div><span className="text-zinc-500">Snapshot:</span> {report.snapshot?.complete ? 'complete' : 'incomplete'}</div>
                    <div><span className="text-zinc-500">Clock:</span> {report.clock_status}</div>
                    <div><span className="text-zinc-500">Zero-trade reason:</span> {report.zero_trade_reason || 'n/a'}</div>
                  </div>
                  <div className="mt-3 space-y-2">{(report.findings || []).map((finding, index) => <div key={`${finding.reason_code}-${index}`} className="rounded border border-zinc-800 p-2 text-xs"><div className="font-semibold">{finding.reason_code} · {finding.severity}</div><div className="text-zinc-400">{finding.summary}</div></div>)}</div>
                </details>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export default ReadinessTrialPanel;
